import { compareAsc, parseISO } from "date-fns";

import { AgentChatMessage } from "./agentChatMessage.types";

export function mergeAgentChatMessagesWithRevisions(
  existingMessages: readonly AgentChatMessage[],
  newMessages: readonly AgentChatMessage[],
): AgentChatMessage[] {
  const mergedMessages: AgentChatMessage[] = [];
  const indexByStaticId = new Map<string, number>();

  const mergeMessage = (message: AgentChatMessage): void => {
    const existingIndex = indexByStaticId.get(message.staticId);
    if (existingIndex == null) {
      indexByStaticId.set(message.staticId, mergedMessages.length);
      mergedMessages.push(message);
      return;
    }

    mergedMessages[existingIndex] = preserveFirstSeenTimestampOnLatestRevision(
      mergedMessages[existingIndex],
      message,
    );
  };

  existingMessages.forEach(mergeMessage);
  newMessages.forEach(mergeMessage);

  if (!messagesAreChronological(mergedMessages)) {
    mergedMessages.sort(compareMessageDates);
  }

  return mergedMessages;
}

function preserveFirstSeenTimestampOnLatestRevision(
  existingMessage: AgentChatMessage,
  newMessage: AgentChatMessage,
): AgentChatMessage {
  const firstMessage =
    compareMessageDates(newMessage, existingMessage) < 0
      ? newMessage
      : existingMessage;
  const latestMessage =
    newMessage.revision >= existingMessage.revision
      ? newMessage
      : existingMessage;

  return {
    ...latestMessage,
    createdDate: firstMessage.createdDate,
    initiatedDate: firstMessage.initiatedDate,
  };
}

function messagesAreChronological(
  messages: readonly AgentChatMessage[],
): boolean {
  return messages.every(
    (message, index) =>
      index === 0 || compareMessageDates(messages[index - 1], message) <= 0,
  );
}

function compareMessageDates(
  first: AgentChatMessage,
  second: AgentChatMessage,
): number {
  return compareAsc(parseISO(first.createdDate), parseISO(second.createdDate));
}
